import { useState, useEffect, useRef, useCallback } from 'react';
import {
  GitBranch, MessageSquare, MessageCircle, Mail, FileText,
  Plus, Pencil, Trash2, RefreshCw, Filter, Send, AtSign,
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CFG = {
  stage_change: { Icon: GitBranch,      dot: '#3b82f6', bg: 'bg-blue-50',   border: 'border-blue-100',   text: 'text-blue-700',   label: 'Mudança de Estágio' },
  interaction:  { Icon: MessageSquare,  dot: '#22c55e', bg: 'bg-green-50',  border: 'border-green-100',  text: 'text-green-700',  label: 'Interação' },
  whatsapp:          { Icon: MessageSquare, dot: '#16a34a', bg: 'bg-green-50',   border: 'border-green-100',  text: 'text-green-800',  label: 'WhatsApp' },
  whatsapp_incoming: { Icon: MessageCircle, dot: '#15803d', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', label: 'WhatsApp (recebido)' },
  email:        { Icon: Mail,           dot: '#60a5fa', bg: 'bg-sky-50',    border: 'border-sky-100',    text: 'text-sky-700',    label: 'E-mail' },
  proposal:     { Icon: FileText,       dot: '#f97316', bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-700', label: 'Proposta' },
  comment:      { Icon: MessageCircle,  dot: '#eab308', bg: 'bg-yellow-50', border: 'border-yellow-100', text: 'text-yellow-700', label: 'Comentário' },
  create:       { Icon: Plus,           dot: '#4ade80', bg: 'bg-green-50',  border: 'border-green-100',  text: 'text-green-600',  label: 'Criação' },
  update:       { Icon: Pencil,         dot: '#94a3b8', bg: 'bg-gray-50',   border: 'border-gray-200',   text: 'text-gray-500',   label: 'Edição' },
  delete:       { Icon: Trash2,         dot: '#ef4444', bg: 'bg-red-50',    border: 'border-red-100',    text: 'text-red-700',    label: 'Exclusão' },
};

const FIELD_LABELS = {
  stage: 'Estágio', esn_id: 'ESN', produto_id: 'Produto',
  valor_saas: 'SaaS (R$)', valor_adesao: 'Adesão (R$)', valor_servico: 'Serviço (R$)',
  previsao_fechamento: 'Previsão', score_bant: 'Score BANT', titulo: 'Título',
  razao_social: 'Razão Social', cnpj: 'CNPJ', contato_nome: 'Contato',
};

const FILTERS = [
  { key: 'all',          label: 'Tudo',              types: null },
  { key: 'messages',     label: 'Mensagens',          types: ['interaction', 'whatsapp', 'email'] },
  { key: 'stage_change', label: 'Mudanças de Estágio', types: ['stage_change'] },
  { key: 'proposal',     label: 'Propostas',           types: ['proposal'] },
  { key: 'comment',      label: 'Comentários',         types: ['comment'] },
];

function formatDateTime(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDayHeader(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Hoje';
  if (dateStr === yesterday) return 'Ontem';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function groupByDay(items) {
  const groups = {};
  for (const item of items) {
    const day = (item.at || '').slice(0, 10) || 'unknown';
    if (!groups[day]) groups[day] = [];
    groups[day].push(item);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

function parsePayload(item) {
  if (!item.payload) return {};
  if (typeof item.payload === 'object') return item.payload;
  try { return JSON.parse(item.payload); } catch { return {}; }
}

// ─── TimelineItem ─────────────────────────────────────────────────────────────

function TimelineItem({ item, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const payload = parsePayload(item);
  // Para WA, usa type_key que distingue incoming vs outgoing para o TYPE_CFG
  const typeKey = item.type === 'whatsapp' && payload.direction === 'incoming'
    ? 'whatsapp_incoming'
    : item.type;
  const cfg = TYPE_CFG[typeKey] || TYPE_CFG.update;
  const { Icon } = cfg;

  const longTextThreshold = 180;

  // Content per type
  let body = null;
  if (item.type === 'stage_change') {
    body = (
      <p className="text-xs text-gray-600">
        <span className="line-through text-red-400">{payload.old_value || '—'}</span>
        {' → '}
        <span className="font-semibold text-blue-700">{payload.new_value || '—'}</span>
      </p>
    );
  } else if (item.type === 'update' && payload.field) {
    const label = FIELD_LABELS[payload.field] || payload.field;
    body = (
      <p className="text-xs text-gray-600">
        <span className="font-medium text-gray-700">{label}:</span>{' '}
        {payload.old_value != null && (
          <><span className="line-through text-red-400">{payload.old_value}</span>{' → '}</>
        )}
        <span className="font-medium text-gray-700">{payload.new_value ?? '—'}</span>
      </p>
    );
  } else if (item.type === 'interaction') {
    const descricao = payload.descricao || '';
    const isLong = descricao.length > longTextThreshold;
    body = (
      <div className="space-y-0.5">
        {payload.canal && (
          <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 mr-1">
            {payload.canal}
          </span>
        )}
        {descricao && (
          <p className="text-xs text-gray-600">
            {isLong && !expanded ? descricao.slice(0, longTextThreshold) + '…' : descricao}
            {isLong && (
              <button type="button" onClick={() => setExpanded(e => !e)}
                className="ml-1 text-[#00DBFF] text-[10px] font-medium hover:underline">
                {expanded ? 'ver menos' : 'ver mais'}
              </button>
            )}
          </p>
        )}
        {payload.proxima_acao_data && (
          <p className="text-[10px] text-gray-400">
            Próxima ação: <span className="font-medium text-gray-600">{payload.proxima_acao_data?.slice(0, 10)}</span>
            {payload.proxima_acao_descricao && ` — ${payload.proxima_acao_descricao}`}
            {Boolean(payload.proxima_acao_concluida) && <span className="ml-1 text-green-600">✓ concluída</span>}
          </p>
        )}
      </div>
    );
  } else if (item.type === 'whatsapp') {
    const msg = payload.message || '';
    const isLong = msg.length > longTextThreshold;
    const isIncoming = payload.direction === 'incoming';
    body = (
      <div className="space-y-0.5">
        {isIncoming && (
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              📩 Recebido
            </span>
            {payload.from_name && (
              <span className="text-[10px] text-gray-500">de {payload.from_name}</span>
            )}
          </div>
        )}
        <p className="text-xs text-gray-600">
          {isLong && !expanded ? msg.slice(0, longTextThreshold) + '…' : msg}
          {isLong && (
            <button type="button" onClick={() => setExpanded(e => !e)}
              className="ml-1 text-[#00DBFF] text-[10px] font-medium hover:underline">
              {expanded ? 'ver menos' : 'ver mais'}
            </button>
          )}
        </p>
        {!isIncoming && payload.status && (
          <span className="text-[10px] text-gray-400">Status: {payload.status}</span>
        )}
        {!isIncoming && payload.phone && (
          <span className="text-[10px] text-gray-400">📞 {payload.phone}</span>
        )}
      </div>
    );
  } else if (item.type === 'proposal') {
    body = (
      <div className="space-y-0.5">
        {payload.title && <p className="text-xs font-semibold text-gray-700">{payload.title}</p>}
        <div className="flex items-center gap-2 flex-wrap">
          {payload.status && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
              {payload.status}
            </span>
          )}
          {payload.valor_total > 0 && (
            <span className="text-[10px] text-gray-500">
              R$ {Number(payload.valor_total).toLocaleString('pt-BR')}
            </span>
          )}
          {payload.public_token && (
            <a
              href={`/proposals/portal/${payload.public_token}`}
              target="_blank"
              rel="noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-[10px] text-[#00DBFF] hover:underline"
            >
              Ver portal →
            </a>
          )}
        </div>
      </div>
    );
  } else if (item.type === 'comment') {
    const content = payload.content || '';
    const isLong = content.length > longTextThreshold;
    body = (
      <p className="text-xs text-gray-700 whitespace-pre-wrap">
        {isLong && !expanded ? content.slice(0, longTextThreshold) + '…' : content}
        {isLong && (
          <button type="button" onClick={() => setExpanded(e => !e)}
            className="ml-1 text-[#00DBFF] text-[10px] font-medium hover:underline">
            {expanded ? 'ver menos' : 'ver mais'}
          </button>
        )}
      </p>
    );
  }

  return (
    <div className="relative pl-8 pb-4">
      {/* Vertical line */}
      {!isLast && <div className="absolute left-[9px] top-5 bottom-0 w-px bg-gray-200" />}
      {/* Dot */}
      <div
        className="absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white"
        style={{ background: cfg.dot }}
      >
        <Icon size={10} className="text-white" />
      </div>

      <div className={`rounded-lg border p-3 ${cfg.bg} ${cfg.border}`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${cfg.text}`}>
            {cfg.label}
          </span>
          <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
            {formatDateTime(item.at)}
          </span>
        </div>
        {item.actor && (
          <p className="text-[10px] text-gray-500 mb-1">
            por <span className="font-medium text-gray-700">{item.actor}</span>
          </p>
        )}
        {body}
      </div>
    </div>
  );
}

// ─── MentionTextarea ──────────────────────────────────────────────────────────

function MentionTextarea({ value, onChange, users }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const ref = useRef(null);

  const filteredUsers = users.filter(u =>
    (u.nome_completo || u.name || u.username || '').toLowerCase().includes(mentionQuery.toLowerCase())
  ).slice(0, 6);

  function handleChange(e) {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    onChange(val);

    // Detect @mention
    const textBefore = val.slice(0, pos);
    const match = textBefore.match(/@(\w*)$/);
    if (match) {
      setMentionStart(match.index);
      setMentionQuery(match[1]);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }

  function pickUser(user) {
    const displayName = user.nome_completo || user.name || user.username;
    const before = value.slice(0, mentionStart);
    const after  = value.slice(ref.current.selectionStart);
    onChange(`${before}@${displayName} ${after}`);
    setShowDropdown(false);
    ref.current?.focus();
  }

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        rows={3}
        placeholder="Escreva um comentário… use @ para mencionar alguém"
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none
                   focus:outline-none focus:border-[#00DBFF] transition-colors"
      />
      {showDropdown && filteredUsers.length > 0 && (
        <ul className="absolute bottom-full mb-1 left-0 right-0 bg-white border border-gray-200
                        rounded-lg shadow-lg z-10 overflow-hidden">
          {filteredUsers.map(u => (
            <li key={u.id}>
              <button
                type="button"
                onMouseDown={() => pickUser(u)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <AtSign size={12} className="text-gray-400" />
                <span>{u.nome_completo || u.name || u.username}</span>
                {u.role && <span className="text-[10px] text-gray-400 ml-auto">{u.role}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── CommentForm ──────────────────────────────────────────────────────────────

function CommentForm({ leadId, users, onCommentAdded }) {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  function extractMentions(text) {
    // Matches @Word or @Word Word (multi-word names, greedy up to next @ or end)
    const matches = [...text.matchAll(/@([\w]+(?:\s[\w]+)*)/gu)];
    const mentioned = [];
    for (const m of matches) {
      const name = m[1].trim();
      const found = users.find(u =>
        (u.nome_completo || u.name || u.username || '').toLowerCase() === name.toLowerCase()
      );
      if (found && !mentioned.includes(found.id)) mentioned.push(found.id);
    }
    return mentioned;
  }

  async function handleSubmit() {
    const trimmed = content.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const mentions = extractMentions(trimmed);
      const comment = await api.createLeadComment(leadId, trimmed, mentions);
      setContent('');
      onCommentAdded(comment);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  }

  if (!user) return null;

  return (
    <div className="border-t border-gray-100 pt-4 space-y-2">
      <MentionTextarea value={content} onChange={setContent} users={users} />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!content.trim() || submitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium
                     bg-[#00DBFF] text-[#002233] hover:bg-[#00c8eb] disabled:opacity-50
                     disabled:cursor-not-allowed transition-colors"
        >
          <Send size={13} />
          Comentar
        </button>
      </div>
    </div>
  );
}

// ─── LeadTimelineTab ──────────────────────────────────────────────────────────

export default function LeadTimelineTab({ lead }) {
  const [items, setItems]           = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter]         = useState('all');
  const [users, setUsers]           = useState([]);
  const sentinelRef                 = useRef(null);
  const hasLoaded                   = useRef(false);

  const loadPage = useCallback(async (cursor = null) => {
    const setter = cursor ? setLoadingMore : setLoading;
    setter(true);
    try {
      const data = await api.getLeadTimeline(lead.id, cursor);
      if (!data) return;
      setItems(prev => cursor ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextCursor(data.next_cursor || null);
    } catch {
      // ignore
    } finally {
      setter(false);
    }
  }, [lead.id]);

  // Initial load
  useEffect(() => {
    if (!lead?.id) return;
    hasLoaded.current = false;
    setItems([]);
    setNextCursor(null);
    loadPage(null).then(() => { hasLoaded.current = true; });
  }, [lead?.id, loadPage]);

  // Fetch users for @mention
  useEffect(() => {
    api.getTeam({ active: true })
      .then(d => setUsers(Array.isArray(d) ? d : (d?.data || [])))
      .catch(() => {});
  }, []);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && nextCursor && !loadingMore && hasLoaded.current) {
          loadPage(nextCursor);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [nextCursor, loadingMore, loadPage]);

  function handleCommentAdded(comment) {
    // Build a timeline item from the created comment and prepend
    const newItem = {
      id: `c-${comment.id}`,
      type: 'comment',
      at: comment.created_at,
      actor: comment.actor_name,
      payload: JSON.stringify({ content: comment.content, mentions: comment.mentions }),
    };
    setItems(prev => [newItem, ...prev]);
  }

  // Apply client-side filter
  const activeCfg   = FILTERS.find(f => f.key === filter);
  const filtered    = activeCfg?.types
    ? items.filter(i => activeCfg.types.includes(i.type))
    : items;
  const grouped     = groupByDay(filtered);

  return (
    <div className="space-y-3">
      {/* Filter pills */}
      <div className="flex items-center gap-1 flex-wrap">
        <Filter size={12} className="text-gray-400 flex-shrink-0" />
        {FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-[#00DBFF]/10 text-[#00DBFF] border border-[#00DBFF]/30'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline grouped by day */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-400 text-sm gap-2">
          <RefreshCw size={16} className="animate-spin opacity-50" />
          Carregando timeline…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-sm gap-2">
          <RefreshCw size={22} className="opacity-30" />
          Nenhum evento registrado ainda.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([day, dayItems]) => (
            <div key={day}>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-gray-100" />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-2">
                  {formatDayHeader(day)}
                </span>
                <div className="h-px flex-1 bg-gray-100" />
              </div>
              <div className="relative ml-4">
                {dayItems.map((item, idx) => (
                  <TimelineItem key={item.id} item={item} isLast={idx === dayItems.length - 1} />
                ))}
              </div>
            </div>
          ))}

            </div>
      )}

      {/* Infinite scroll sentinel — always mounted so observer is stable */}
      <div ref={sentinelRef} className="h-4" />
      {loadingMore && (
        <div className="flex items-center justify-center py-2 text-gray-400 text-xs gap-1">
          <RefreshCw size={12} className="animate-spin" />
          Carregando mais…
        </div>
      )}

      {/* Comment form — always visible, at the bottom */}
      <CommentForm leadId={lead.id} users={users} onCommentAdded={handleCommentAdded} />
    </div>
  );
}
